import React, { useRef, useState } from 'react';
import {
  updateKnowledgeGeneratorCompetitorDraft,
  type KnowledgeGeneratorCompetitorDraft,
  type KnowledgeGeneratorTask,
} from '../../services/apiService';
import { CompetitorCategory } from '../../types/competitor';
import { hasUnresolvedUncertaintyLabel } from '../../utils/uncertaintyLabels';

const COMPETITOR_TYPE_LABELS: Record<string, string> = {
  direct_competitor: 'Direct competitor',
  indirect_competitor: 'Indirect competitor',
  alternative_solution: 'Alternative solution',
};

export const competitorDraftVersion = (draft: KnowledgeGeneratorCompetitorDraft) => JSON.stringify({
  name: draft.name || '',
  advantages: (draft.advantages || []).map(({ point, category, evidence, priority }) => ({
    point,
    category: category || 'other',
    evidence: evidence || '',
    priority: priority ?? 3,
  })),
  weaknesses: (draft.weaknesses || []).map(({ point, category }) => ({
    point,
    category: category || 'other',
  })),
  talking_points: draft.talking_points || '',
  category: draft.category || 'direct_competitor',
});

const mergeEditedPoints = <T extends { point: string }>(
  text: string,
  existing: T[],
  create: (point: string) => T,
): T[] => {
  // Each line is one client-authored claim. Preserve it exactly; use trim
  // only to decide whether the line is empty.
  const points = text.split('\n').filter((point) => point.trim());
  const unusedIndexes = new Set(existing.map((_, index) => index));

  return points.map((point, position) => {
    const exactIndex = existing.findIndex((item, index) => unusedIndexes.has(index) && item.point === point);
    const sourceIndex = exactIndex >= 0
      ? exactIndex
      : (unusedIndexes.has(position) ? position : -1);
    if (sourceIndex < 0) return create(point);
    unusedIndexes.delete(sourceIndex);
    return { ...existing[sourceIndex], point };
  });
};

interface CompetitorDraftCardProps {
  taskId: string;
  draft: KnowledgeGeneratorCompetitorDraft;
  onToggle: () => void;
  onRemove: () => void;
  onSaved: (task: KnowledgeGeneratorTask) => void;
  onError: (message: string) => void;
  selectionContext?: 'starter set' | 'publish';
  selectionDisabled?: boolean;
}

const CompetitorDraftCard: React.FC<CompetitorDraftCardProps> = ({
  taskId,
  draft,
  onToggle,
  onRemove,
  onSaved,
  onError,
  selectionContext = 'starter set',
  selectionDisabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [edit, setEdit] = useState({ name: '', advantages: '', weaknesses: '', talkingPoints: '' });

  const startEditing = () => {
    setEdit({
      name: draft.name || '',
      advantages: (draft.advantages || []).map((item) => item.point).join('\n'),
      weaknesses: (draft.weaknesses || []).map((item) => item.point).join('\n'),
      talkingPoints: draft.talking_points || '',
    });
    setEditing(true);
  };

  const save = async () => {
    if (!edit.name.trim() || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const updatedTask = await updateKnowledgeGeneratorCompetitorDraft(
        taskId,
        draft.tempId,
        {
          name: edit.name.trim(),
          advantages: mergeEditedPoints(
            edit.advantages,
            draft.advantages || [],
            (point) => ({ point, category: CompetitorCategory.OTHER }),
          ),
          weaknesses: mergeEditedPoints(
            edit.weaknesses,
            draft.weaknesses || [],
            (point) => ({ point, category: CompetitorCategory.OTHER }),
          ),
          talking_points: edit.talkingPoints,
          edited: true,
        },
        competitorDraftVersion(draft),
      );
      onSaved(updatedTask);
      setEditing(false);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Could not save this competitor.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg border p-2.5 ${hasUnresolvedUncertaintyLabel(draft) ? 'border-warning bg-warning-tint' : 'border-hairline bg-surface-subtle'}`}>
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Include competitor ${draft.name} in ${selectionContext}`}
            checked={draft.approved}
            disabled={draft.synced || selectionDisabled}
            onChange={onToggle}
            className="mt-0.5 h-4 w-4 rounded border-ink-border text-signature"
          />
          <div className="min-w-0">
            <div className="truncate text-xs font-medium text-ink">{draft.name}</div>
            <div className="mt-0.5 text-[11px] text-ink-muted">
              {COMPETITOR_TYPE_LABELS[draft.category] || draft.category}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
              {draft.synced ? 'Published' : draft.reviewedAt ? 'Reviewed; not live yet' : 'Review required before publishing'}
              {hasUnresolvedUncertaintyLabel(draft) && (
                <span className="rounded-full bg-warning-tint px-1.5 py-0.5 font-semibold text-ink">Needs client clarification</span>
              )}
              {!draft.synced && draft.styleCleanupStatus === 'pending' && (
                <span className="rounded-full bg-info-tint px-1.5 py-0.5 text-info">Polishing…</span>
              )}
              {!draft.synced && draft.styleCleanupStatus === 'cleaned' && (
                <span className="rounded-full bg-success-tint px-1.5 py-0.5 text-success">Polished</span>
              )}
            </div>
            {!!draft.advantages?.length && (
              <div className="mt-1.5 text-[11px] text-ink-secondary">
                <span className="font-medium text-success">Where you win: </span>
                {draft.advantages.map((item) => item.point).join('; ')}
              </div>
            )}
            {!!draft.weaknesses?.length && (
              <div className="mt-1 text-[11px] text-ink-secondary">
                <span className="font-medium text-ink-secondary">Their weak points: </span>
                {draft.weaknesses.map((item) => item.point).join('; ')}
              </div>
            )}
            {draft.talking_points && (
              <div className="mt-1 text-[11px] text-ink-secondary">
                <span className="font-medium">Talking points: </span>{draft.talking_points}
              </div>
            )}
            {editing && (
              <div className="mt-3 space-y-3 rounded-xl border border-hairline bg-surface p-4" onClick={(event) => event.stopPropagation()}>
                <p className="text-sm font-semibold text-ink">Edit competitor</p>
                <label className="block text-sm font-medium text-ink-secondary">Competitor name
                  <input aria-label="Competitor name" value={edit.name} onChange={(event) => setEdit((value) => ({ ...value, name: event.target.value }))} className="mt-1.5 min-h-[44px] w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Where you win
                  <textarea aria-label="Where you win" value={edit.advantages} onChange={(event) => setEdit((value) => ({ ...value, advantages: event.target.value }))} rows={3} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Their weak points
                  <textarea value={edit.weaknesses} onChange={(event) => setEdit((value) => ({ ...value, weaknesses: event.target.value }))} rows={3} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Talking points
                  <textarea value={edit.talkingPoints} onChange={(event) => setEdit((value) => ({ ...value, talkingPoints: event.target.value }))} rows={3} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={saving || !edit.name.trim()} onClick={save} className="min-h-[44px] rounded-lg bg-signature px-4 text-sm font-semibold text-on-signature hover:bg-signature-strong disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-signature">{saving ? 'Saving…' : 'Save competitor'}</button>
                  <button type="button" onClick={() => setEditing(false)} className="min-h-[44px] rounded-lg px-3 text-sm font-medium text-ink-secondary hover:bg-surface-sunken hover:text-ink">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
        {!draft.synced && (
          <div role="group" aria-label={`Actions for competitor ${draft.name}`} className="flex shrink-0 items-center gap-1">
            <button type="button" aria-label={`Edit competitor ${draft.name}`} onClick={(event) => { event.stopPropagation(); startEditing(); }} className="min-h-[44px] rounded px-2 text-[11px] font-medium text-signature hover:bg-signature-tint hover:text-signature-strong focus:outline-none focus:ring-2 focus:ring-signature">Edit</button>
            <button type="button" aria-label={`Remove competitor ${draft.name}`} onClick={(event) => { event.stopPropagation(); onRemove(); }} className="min-h-[44px] rounded px-2 text-[11px] font-medium text-ink-muted hover:bg-error-tint hover:text-error focus:outline-none focus:ring-2 focus:ring-error">Remove</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorDraftCard;
